import React, { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import Header from './components/Header10';
import Footer from './components/Footer10';
import ProductCard from './components/ProductCard';

const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 10000]);

    const categories = ['All', 'Women', 'Men', 'Accessories'];
    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest First' },
    ];

    const products = [
        {
            id: 1,
            name: 'Essential Cotton Tee',
            category: 'Women',
            price: 1299,
            image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600',
        },
        {
            id: 2,
            name: 'Classic Oxford Shirt',
            category: 'Men',
            price: 2499,
            image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600',
        },
        {
            id: 3,
            name: 'Minimalist Leather Bag',
            category: 'Accessories',
            price: 3999,
            originalPrice: 4999,
            image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600',
        },
        {
            id: 4,
            name: 'Simple Canvas Sneakers',
            category: 'Women',
            price: 2999,
            image: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600',
        },
        {
            id: 5,
            name: 'Organic Linen Dress',
            category: 'Women',
            price: 3499,
            image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600',
        },
        {
            id: 6,
            name: 'Classic Watch',
            category: 'Accessories',
            price: 4999,
            image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600',
        },
        {
            id: 7,
            name: 'Relaxed Denim',
            category: 'Men',
            price: 2799,
            image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=600',
        },
        {
            id: 8,
            name: 'Timeless Boots',
            category: 'Women',
            price: 5999,
            originalPrice: 7999,
            image: 'https://images.unsplash.com/photo-1638247025967-b4e38f787b76?w=600',
        },
        {
            id: 9,
            name: 'Clean Backpack',
            category: 'Accessories',
            price: 2499,
            image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600',
        },
        {
            id: 10,
            name: 'Soft Cashmere Scarf',
            category: 'Accessories',
            price: 1899,
            image: 'https://images.unsplash.com/photo-1520903920243-00d872a2d1c9?w=600',
        },
        {
            id: 11,
            name: 'Pure Wool Sweater',
            category: 'Men',
            price: 3799,
            image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=600',
        },
        {
            id: 12,
            name: 'Simple Tote',
            category: 'Accessories',
            price: 1999,
            image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600',
        },
    ];

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());

    return (
        <div className="min-h-screen bg-white">
            <Header cartCount={3} />

            {/* Hero Section - DAWN style */}
            <div className="bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
                    <h1 className="text-5xl lg:text-7xl font-light text-gray-900 mb-6">
                        Shop Collection
                    </h1>
                    <p className="text-lg lg:text-xl text-gray-600 font-light max-w-2xl mx-auto">
                        Thoughtfully designed essentials for everyday living
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20">
                {/* Search and Controls */}
                <div className="flex flex-col md:flex-row gap-6 mb-12 items-center">
                    {/* Search */}
                    <div className="flex-1 w-full md:max-w-md">
                        <div className="relative">
                            <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search"
                                className="w-full pl-10 pr-4 py-3 border-b-2 border-gray-200 focus:outline-none focus:border-gray-900 transition-colors bg-transparent text-gray-900 placeholder-gray-400"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4 items-center">
                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-6 py-3 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors bg-white text-gray-900"
                        >
                            {sortOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>

                        {/* Filter Toggle */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="px-6 py-3 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors flex items-center gap-2"
                        >
                            <SlidersHorizontal size={18} />
                            <span>Filter</span>
                        </button>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-gray-50 border border-gray-200 p-8 lg:p-12 mb-16">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className="text-2xl font-light text-gray-900">Filter Products</h3>
                            <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-900 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                            {/* Price Range */}
                            <div>
                                <h4 className="font-light mb-4 text-gray-900 text-lg">Price Range</h4>
                                <div className="space-y-3">
                                    <input
                                        type="range"
                                        min="0"
                                        max="10000"
                                        value={priceRange[1]}
                                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                                        className="w-full"
                                    />
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>₹0</span>
                                        <span>₹{priceRange[1]}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Size */}
                            <div>
                                <h4 className="font-light mb-4 text-gray-900 text-lg">Size</h4>
                                <div className="flex flex-wrap gap-3">
                                    {['XS', 'S', 'M', 'L', 'XL'].map(size => (
                                        <button
                                            key={size}
                                            className="w-14 h-14 border border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-light"
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Color */}
                            <div>
                                <h4 className="font-light mb-4 text-gray-900 text-lg">Color</h4>
                                <div className="flex flex-wrap gap-3">
                                    {[
                                        { name: 'Black', value: '#000000' },
                                        { name: 'White', value: '#FFFFFF' },
                                        { name: 'Gray', value: '#808080' },
                                        { name: 'Beige', value: '#F5F5DC' }
                                    ].map(color => (
                                        <button
                                            key={color.value}
                                            className="w-14 h-14 border-2 border-gray-300 hover:border-gray-900 transition-colors"
                                            style={{ backgroundColor: color.value }}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Pills */}
                <div className="flex flex-wrap justify-center gap-4 mb-16">
                    {categories.map(category => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category.toLowerCase())}
                            className={`px-8 py-3 text-sm font-light transition-colors ${
                                selectedCategory === category.toLowerCase()
                                    ? 'bg-gray-900 text-white'
                                    : 'bg-white border border-gray-300 text-gray-900 hover:border-gray-900'
                            }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {/* Products Grid - Spacious DAWN layout */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-12 mb-20">
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>

                {/* Load More - Simple and clean */}
                <div className="text-center">
                    <button className="px-12 py-4 border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-light text-sm tracking-wide">
                        VIEW MORE
                    </button>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Shop;
